import React, {useContext, useState, useEffect, useRef, useCallback} from 'react'
import ReactDOM from 'react-dom'
import _ from 'lodash/fp'
import {keyframes} from 'styled-components/macro'
import {ADMIN, PATIENT} from "../../enums/UserRoles"
import {UploaderConfigContext} from "../../service/UploaderConfigContext"
import {useUIDataSelector} from "../../service/hooks/useUIDataSelector"
import {useDoAction} from "../../service/hooks/useDoAction"
import {Failed, Uploaded} from "../../service/enums/RecordStatus"
import {Accepted} from "../../enums/RecordState"
import {UIAction} from "../../service/UIAction"
import {useRecordsSelector} from "../../service/hooks/useRecords"
import {getItemPersistedUID, getRecordIcon, isDicom} from "../../service/utils/recordUtils"
import {DEVICE, DISK} from "../../enums/UploaderStepsManagement"
import {callIfFunction} from "@startlibs/utils"
import {Button, Icon} from "@startlibs/components"
import {ConfirmDialog, useConfirmDialog} from "@startlibs/form"
import {RecordRowActions} from "./RecordRowActions"
import {RecordRowQuarantinedFooter} from "./RecordRowQuarantinedFooter"
import {RecordRowProgressStatus} from "./RecordRowProgressStatus"
import {DicomStudy} from "../../enums/RecordFormat"
import {FormattedMessage} from "react-intl"
import {getShortTokenUrl} from "../../service/utils/endpointUtils"
import {NotesField} from "./NotesField"
import {MetadataField} from "./MetadataField"
import {ICON_ERROR_HEX, ICON_THUMBS_DOWN, ICON_THUMBS_UP} from '../../../../../purview-design/src/refs/Icons'
import {FeedbackPanel} from '../../../../../purview-design/src/components/AiPanelEV'

import {useAiSummary, getEveLoadingLabel} from './ai/useAiSummary'
import {StructuredSummaryRenderer, AiSummaryModal, RelevanceBarSvg, OpenOutsideIcon} from './ai/AiSummaryRenderer'
import {
  CardShell,
  RowContent,
  InfoPanel,
  CaseInfo,
  RecordIconWrap,
  RecordIcon,
  BasicFields,
  AddActionsRow,
  DetailsInlineButton,
  InfoPanelRight,
  DetailsPanelOuter,
  DetailsPanelInner,
  DetailsPanel,
  DetailsLeftCol,
  DetailsRightCol,
  DicomDetailRow,
  DicomDetailLabel,
  DicomDetailValue,
  AuthorStrip,
  MoverHandle,
  GripIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from './RecordRowStyles'
import {
  AiSummaryWrap,
  AiSummaryHeading,
  AnimatedDots,
  TryAgainLink,
  AiErrorWrap,
  AiErrorHeading,
  AiSummaryScrollOuter,
  AiSummaryScrollArea,
  AiSummaryScrollFade,
  AiSummaryText,
  RelevanceBadgeWrap,
  RelevanceLabel,
  RelevanceBarRow,
  RelevanceBarLabels,
  RelevanceBarLabel,
  RelevancyExplanationChip,
  RelevancyExplanationPopover,
  RelevancyExplanationText,
  ExplanationRatingRow,
  ExplanationRatingLabel,
  ExplanationThumbsRow,
  ExplanationThumbBtn,
  AiErrorText,
  OpenOutsideButton,
} from './ai/AiSummaryStyles'

// ── Date formatting utilities ────────────────────────────────────────────────

const getOrdinalDay = (day) => {
  const mod100 = day % 100
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`
  const mod10 = day % 10
  if (mod10 === 1) return `${day}st`
  if (mod10 === 2) return `${day}nd`
  if (mod10 === 3) return `${day}rd`
  return `${day}th`
}

const formatAuthorDate = (inputDate) => {
  const parsedDate = new Date(inputDate)
  if (Number.isNaN(parsedDate.getTime())) return null

  const month = parsedDate.toLocaleString('en-US', {month: 'short'})
  const day = getOrdinalDay(parsedDate.getDate())
  const year = parsedDate.getFullYear()
  const hours = String(parsedDate.getHours()).padStart(2, '0')
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0')
  return `${month} ${day} ${year} - ${hours}:${minutes}`
}

// ── Main export ──────────────────────────────────────────────────────────────

export const RecordRowContainer = (
  {
    record,
    group,
    dragProvided,
    children,

    cancelUpload,
    deleteRecord,
    openDetails,

    withNotes = true,
    withMeta = true
  }) => {
  const uploaderConfig = useContext(UploaderConfigContext)
  const {isApp, mode, allowReorder, role, locations, setSelectedDirectMessageRecords, requestId, worklistViewerJwt, adminUserEmail, eveAiSettings, isSelectingRecordsDirectMessage} = uploaderConfig
  const doAction = useDoAction()
  const recordKeys = useRecordsSelector(_.map(getItemPersistedUID))
  const isSelectMode = useUIDataSelector('selectMode')
  const selectedRecords = useUIDataSelector('selectedRecords')
  const groupsLength = useUIDataSelector('groups.length')

  const [isExpanded, setIsExpanded] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExplanationPopoverOpen, setIsExplanationPopoverOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState(null)
  const chipRef = useRef(null)
  const popoverRef = useRef(null)
  const [isPmExplanationOpen, setIsPmExplanationOpen] = useState(false)
  const [pmPopoverPos, setPmPopoverPos] = useState(null)
  const pmChipRef = useRef(null)
  const pmPopoverRef = useRef(null)
  const [relevancyRating, setRelevancyRating] = useState(null)
  const [patientMatchRating, setPatientMatchRating] = useState(null)
  const [feedbackState, setFeedbackState] = useState('closed')
  const [feedbackFeature, setFeedbackFeature] = useState(null)
  const [feedbackRating, setFeedbackRating] = useState(null)
  const [feedbackSourceExplanation, setFeedbackSourceExplanation] = useState(null)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [summaryIsTall, setSummaryIsTall] = useState(false)
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const [hasScrollOverflow, setHasScrollOverflow] = useState(false)
  const summaryContentRef = useRef(null)
  const summaryScrollRef = useRef(null)
  const {
    aiState,
    relevanceScore,
    patientMismatch,
    summaryParagraphs,
    summaryData,
    summaryErrorMessage,
    aiErrorMessage,
    eveStatus,
    relevancyClassification,
    relevancyExplanation,
    patientMatchDetermination,
    patientMatchExplanation,
    patientMatchConfidence,
    eveDocumentId,
    patientMatchError,
    relevancyError,
    patientMatchLoading,
    relevancyLoading,
    retryAll,
    retryPatientMatch,
    retryRelevancy,
    retryBothFeatures,
    submitFeatureFeedback,
    cancelSummary,
    fakeCancelSummary,
    cancelFeatures,
    loadFeatures,
    canCancel,
    canRetry,
    showFeatureCancelLink,
    featuresCancelled,
    featuresLoading,
    summaryCancelled,
  } = useAiSummary({
    isExpanded,
    requestId,
    recordId: record.recordUID,
    authJwt: worklistViewerJwt,
  })
  const confirmCancelSummary = useConfirmDialog(
    <ConfirmDialog
      title="Cancel AI processing"
      action={cancelSummary}
      confirm={<Button alert>Cancel processing</Button>}
      cancelLabel="Keep processing"
    >
      <p>The AI summary will stop processing for this medical record. You will be able to reload it later.</p>
    </ConfirmDialog>
  )
  const confirmFakeCancelSummary = useConfirmDialog(
    <ConfirmDialog
      title="Cancel AI processing"
      action={fakeCancelSummary}
      confirm={<Button alert>Cancel processing</Button>}
      cancelLabel="Keep processing"
    >
      <p>The AI summary will stop processing for this medical record. You will be able to reload it later.</p>
    </ConfirmDialog>
  )

  const loadingLabel = getEveLoadingLabel(eveStatus)
  const hasRelevancyData = relevanceScore != null || Boolean(relevancyClassification) || Boolean(relevancyExplanation)
  const hasPatientMatchData = Boolean(patientMatchDetermination) || patientMismatch
  const hideAiFeedbacks = typeof window !== 'undefined' && ['true', '1'].includes(String(window.HIDE_AI_FEEDBACKS).toLowerCase())

  // After AI summary renders: measure content height and detect overflow
  useEffect(() => {
    if (aiState === 'done') {
      const el = summaryScrollRef.current
      if (el) {
        const overflows = el.scrollHeight > el.clientHeight
        setHasScrollOverflow(overflows)
        setSummaryIsTall(el.scrollHeight > 200)
      }
      setScrolledToBottom(false)
    } else {
      setSummaryIsTall(false)
      setScrolledToBottom(false)
      setHasScrollOverflow(false)
    }
  }, [aiState, summaryParagraphs.length])

  // Close details and AI modal when entering select mode
  useEffect(() => {
    if (isSelectMode) {
      setIsExpanded(false)
      setIsModalOpen(false)
    }
  }, [isSelectMode])

  // Close explanation popover on outside click or scroll
  useEffect(() => {
    if (!isExplanationPopoverOpen) return
    if (feedbackState !== 'closed') return
    const close = () => setIsExplanationPopoverOpen(false)
    const handleClickOutside = (e) => {
      if (
        !chipRef.current?.contains(e.target) &&
        !popoverRef.current?.contains(e.target)
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', close, true)
    }
  }, [isExplanationPopoverOpen, feedbackState])

  // Close patient match explanation popover on outside click or scroll
  useEffect(() => {
    if (!isPmExplanationOpen) return
    if (feedbackState !== 'closed') return
    const close = () => setIsPmExplanationOpen(false)
    const handleClickOutside = (e) => {
      if (
        !pmChipRef.current?.contains(e.target) &&
        !pmPopoverRef.current?.contains(e.target)
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', close, true)
    }
  }, [isPmExplanationOpen, feedbackState])

  const handleSummaryScroll = useCallback(() => {
    const el = summaryScrollRef.current
    if (!el) return
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4
    setScrolledToBottom(atBottom)
  }, [])

  const handleExplanationRating = useCallback((feature, next, explanation) => {
    if (feature === 'relevancy') {
      setRelevancyRating(next)
    } else if (feature === 'patient-match') {
      setPatientMatchRating(next)
    }

    if (!next) return
    setFeedbackFeature(feature)
    setFeedbackRating(next)
    setFeedbackSourceExplanation(explanation || null)
    setFeedbackMessage('')
    setFeedbackState('open')
  }, [])

  const closeFeedbackPopup = useCallback(() => {
    if (feedbackState === 'sending') return
    setFeedbackState('closed')
    setFeedbackFeature(null)
    setFeedbackRating(null)
    setFeedbackSourceExplanation(null)
    setFeedbackMessage('')
  }, [feedbackState])

  const submitExplanationFeedback = useCallback(async () => {
    if (!feedbackFeature || !feedbackRating || feedbackState === 'sending') return
    setFeedbackState('sending')
    const ok = await submitFeatureFeedback({
      feature: feedbackFeature,
      rating: feedbackRating,
      explanation: feedbackSourceExplanation,
      comment: feedbackMessage.trim(),
    })
    if (ok) {
      setFeedbackState('success')
      return
    }
    setFeedbackState('error')
  }, [
    feedbackFeature,
    feedbackRating,
    feedbackState,
    feedbackSourceExplanation,
    feedbackMessage,
    submitFeatureFeedback,
  ])

  const backToFeedbackForm = useCallback(() => {
    setFeedbackState('open')
  }, [])

  const openViewerAtPage = useCallback((page, highlightText) => {
    const shortTokenUrl = getShortTokenUrl(uploaderConfig)
    if (!shortTokenUrl) return
    const wi = window.open('about:blank', '_blank')
    if (!wi) return
    shortTokenUrl(requestId)
      .then((response) => {
        if (!response?.storageHost || !response?.jwt) {
          wi.close()
          return
        }
        const pageParam = page != null ? `&page=${page}` : ''
        const highlightParam = highlightText ? `&highlight=${encodeURIComponent(highlightText)}` : ''
        const finalUrl = response.storageHost + `/view/record/${record.recordUID}?t=${response.jwt}${pageParam}${highlightParam}`
        wi.location.href = finalUrl
      })
      .catch(() => {
        wi.close()
      })
  }, [uploaderConfig, requestId, record.recordUID])


  const isDragDisabled = !allowReorder || (recordKeys.length < 2 && groupsLength === 1)
  const isMinified = mode === DEVICE || mode === DISK
  const isCompactMinifiedUpload = role === PATIENT && isMinified
  const uploadDate = formatAuthorDate(record.uploadDate) || formatAuthorDate(record.info?.uploadDate)
  const rawSubmittedBy = record.stateContext?.Submitted?.by
  const submittedBy = rawSubmittedBy
    ? rawSubmittedBy.includes('@')
      ? rawSubmittedBy
      : rawSubmittedBy.includes('Patient')
        ? 'Patient'
        : locations?.find(({id}) => id == rawSubmittedBy)?.name || null
    : null
  const isDicomRecord = isDicom(record) || record.recordFormat === DicomStudy || record.info?.type === DicomStudy
  const aiVisibility = eveAiSettings?.visibility
  const aiAllowedExtensions = eveAiSettings?.allowedExtensions || []
  const recordExtension = (record.fileExtension || '').toLowerCase()
  const isAllowedExtension = aiAllowedExtensions.length > 0 && aiAllowedExtensions.includes(recordExtension)
  const isAllowedEmailDomain = adminUserEmail && (adminUserEmail.endsWith('@purview.net') || adminUserEmail.endsWith('@healthmark-group.com'))
  const supportsDetails = isAllowedExtension && (
    aiVisibility === 'SHOW' ||
    (aiVisibility === 'PURVIEW' && isAllowedEmailDomain)
  ) //|| isDicomRecord
  const isAiBlockedByRisk = Boolean(record.quarantined || record.partiallyQuarantined)
  const isRecordReady = record.status === Uploaded || record.status === Accepted
  const canShowDetailsButton = role === ADMIN && !isMinified && supportsDetails && !isSelectMode && isRecordReady
  const canOpenDetails = canShowDetailsButton && !isAiBlockedByRisk
  const documentName = record.description || record.info?.description || record.fileName || record.info?.fileName || null
  const referringPhysician = record.info?.referringPhysician || record.info?.ReferringPhysicianName || '-'
  const studyUID = record.info?.studyUID || record.info?.StudyUID || '-'
  const hasNote = Boolean(record.notes?.note)
  const hasMetadata = Object.keys(record.metadata || {}).length > 0
  const showInlineAddActions = role === ADMIN && withNotes && withMeta && record.recordUID && !hasNote && !hasMetadata

  const sharedProps = {
    failedUpload: record.status === Failed,
    unavailable: record.quarantined,
    isSelected: selectedRecords.includes(record.key),
    isSelectMode,
    mode,
  }

  return <CardShell
    {...sharedProps}
    compactMinified={isCompactMinifiedUpload}
    ref={dragProvided.innerRef}
    {...dragProvided.draggableProps}
    {...(isDragDisabled ? dragProvided.dragHandleProps : {})}
    onClick={isSelectMode ? (() => {
      setSelectedDirectMessageRecords(selectedDirectMessageRecords => {
        if (selectedDirectMessageRecords.includes(record.key)) {
          return selectedDirectMessageRecords.filter(file => file !== record.key)
        } else {
          return [...selectedDirectMessageRecords, record.key]
        }
      })
      return doAction(UIAction.ToggleSelectedRecord, record.key)
    }) : undefined}
  >
    {!isDragDisabled && (
      <MoverHandle {...dragProvided.dragHandleProps}>
        <GripIcon />
      </MoverHandle>
    )}

    <RowContent>
      {/* Info panel */}
      <InfoPanel {...sharedProps} compactMinified={isCompactMinifiedUpload}>
        <CaseInfo compactMinified={isCompactMinifiedUpload} selectingDM={isSelectingRecordsDirectMessage}>
          <RecordIconWrap compactMinified={isCompactMinifiedUpload}>
            <RecordIcon icon={getRecordIcon(record)} mode={mode} compactMinified={isCompactMinifiedUpload} selectingDM={isSelectingRecordsDirectMessage}/>
          </RecordIconWrap>
          <BasicFields>
            {callIfFunction(children, isMinified)}
            {showInlineAddActions ? (
              <AddActionsRow>
                <NotesField record={record}/>
                <MetadataField record={record}/>
              </AddActionsRow>
            ) : (
              <>
                {role === ADMIN && withNotes && record.recordUID && <NotesField record={record}/>}
                {role === ADMIN && withMeta && record.recordUID && <MetadataField record={record}/>}
              </>
            )}
          </BasicFields>
        </CaseInfo>

        <InfoPanelRight compactMinified={isCompactMinifiedUpload}>
          <RecordRowActions
            group={group}
            record={record}
            isMinified={isMinified}
            isDragDisabled={isDragDisabled}
            deleteRecord={deleteRecord}
            isExpanded={isExpanded}
            eveDocumentId={eveDocumentId}
            onStartSummarization={() => setIsExpanded(true)}
            canOpenAiPanel={canOpenDetails}
            onOpenPage={openViewerAtPage}
            aiReady={aiState === 'done'}
            aiProcessingLabel={aiState === 'loading' ? loadingLabel : null}
          />
          <RecordRowProgressStatus
            openDetails={openDetails}
            record={record}
            isMinified={isMinified}
            cancelUpload={cancelUpload}
          />
          {canShowDetailsButton && (
            <DetailsInlineButton
              disabled={!canOpenDetails}
              aria-disabled={!canOpenDetails}
              title={!canOpenDetails ? 'Details is unavailable for quarantined or suspected harmful files' : undefined}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!canOpenDetails) {
                  return
                }
                setIsExpanded(v => !v)
              }}
            >
              <FormattedMessage
                defaultMessage="Details"
                description="Uploader, details toggle button"
              />
              {isExpanded ? <ChevronUpIcon/> : <ChevronDownIcon/>}
            </DetailsInlineButton>
          )}
        </InfoPanelRight>
      </InfoPanel>

      {/* Expanded details panel */}
      {supportsDetails && !isSelectMode && canOpenDetails && (
        <DetailsPanelOuter isOpen={isExpanded}>
          <DetailsPanelInner>
            <DetailsPanel>
          <DetailsLeftCol>
            {/* DICOM extra details */}
            {isDicomRecord && (
              <>
                <DicomDetailRow>
                  <DicomDetailLabel><FormattedMessage defaultMessage="Referring physician" description="Uploader, DICOM details referring physician label" />:</DicomDetailLabel>
                  <DicomDetailValue>{referringPhysician}</DicomDetailValue>
                </DicomDetailRow>
                <DicomDetailRow>
                  <DicomDetailLabel><FormattedMessage defaultMessage="Study UID" description="Uploader, DICOM details study UID label" />:</DicomDetailLabel>
                  <DicomDetailValue>{studyUID}</DicomDetailValue>
                </DicomDetailRow>
              </>
            )}

            {/* AI Summary */}
            {aiState === 'loading' && (
              <AiSummaryWrap>
                <AiSummaryHeading css="color:#999999;font-style:normal;">
                  <Icon icon={"stars-circle"} /> {loadingLabel}<AnimatedDots />
                  {canCancel && (
                    <TryAgainLink
                      css={`animation: ${keyframes`from{opacity:0}to{opacity:1}`} 300ms ease-out;`}
                      onClick={confirmCancelSummary}
                    >
                      Cancel
                    </TryAgainLink>
                  )}
                  {!canCancel && canRetry && eveStatus === 'SUMMARIZING' && (
                    <TryAgainLink
                      css={`animation: ${keyframes`from{opacity:0}to{opacity:1}`} 300ms ease-out;`}
                      onClick={confirmFakeCancelSummary}
                    >
                      Cancel
                    </TryAgainLink>
                  )}
                </AiSummaryHeading>
              </AiSummaryWrap>
            )}
            {aiState === 'error' && !summaryCancelled && (
              <AiErrorWrap>
                <AiErrorHeading alignEnd>
                  <Icon icon={ICON_ERROR_HEX} /> Error loading AI summary
                  {canRetry && <TryAgainLink onClick={retryAll}>Try again</TryAgainLink>}
                </AiErrorHeading>
              </AiErrorWrap>
            )}
            {summaryCancelled && (
              <AiSummaryWrap>
                <TryAgainLink onClick={retryAll}>
                  <Icon icon={"stars-circle"} css="margin-right:4px;" /> Load document summary
                </TryAgainLink>
              </AiSummaryWrap>
            )}
            {aiState === 'done' && summaryErrorMessage && (
              <AiErrorWrap>
                <AiErrorHeading alignEnd><Icon icon={ICON_ERROR_HEX} /> Error loading AI summary</AiErrorHeading>
              </AiErrorWrap>
            )}
            {aiState === 'done' && !summaryErrorMessage && (
              <AiSummaryWrap>
                <AiSummaryHeading><Icon icon={"stars-circle"} /> AI Summary</AiSummaryHeading>
                <AiSummaryScrollOuter>
                  <AiSummaryScrollArea ref={summaryScrollRef} onScroll={handleSummaryScroll}>
                    <div ref={summaryContentRef}>
                      {summaryData && (summaryData.title || summaryData.sections) ? (
                        <StructuredSummaryRenderer data={summaryData} onOpenPage={openViewerAtPage} eveDocumentId={eveDocumentId} authJwt={worklistViewerJwt} documentName={documentName} />
                      ) : (
                        (summaryParagraphs.length ? summaryParagraphs : ['No summary available.']).map((para, i) => (
                          <AiSummaryText key={i} css="margin-bottom:10px;">
                            {para}
                          </AiSummaryText>
                        ))
                      )}
                    </div>
                  </AiSummaryScrollArea>
                  {hasScrollOverflow && !scrolledToBottom && <AiSummaryScrollFade />}
                </AiSummaryScrollOuter>
                {summaryIsTall && (
                  <OpenOutsideButton onClick={() => setIsModalOpen(true)}>
                    Open outside <OpenOutsideIcon />
                  </OpenOutsideButton>
                )}
              </AiSummaryWrap>
            )}
          </DetailsLeftCol>

          {/* Case Relevance */}
          <DetailsRightCol>
            {!hasRelevancyData && !hasPatientMatchData && !patientMatchLoading && !relevancyLoading && !featuresCancelled && (aiState === 'loading' || featuresLoading) && (
              <RelevanceBadgeWrap>
                <RelevanceLabel css="color:#999999;font-style:normal;"><Icon icon={"stars-circle"} /> AI is processing<AnimatedDots /></RelevanceLabel>
                {showFeatureCancelLink && (
                  <TryAgainLink
                    css={`padding-left:20px;animation: ${keyframes`from{opacity:0}to{opacity:1}`} 300ms ease-out;`}
                    onClick={cancelFeatures}
                  >
                    Cancel
                  </TryAgainLink>
                )}
              </RelevanceBadgeWrap>
            )}
            {!hasRelevancyData && !hasPatientMatchData && featuresCancelled && (
              <RelevanceBadgeWrap>
                <TryAgainLink onClick={loadFeatures}>
                  <Icon icon={"stars-circle"} css="margin-right:4px;" /> Load AI information
                </TryAgainLink>
              </RelevanceBadgeWrap>
            )}
            {!hasRelevancyData && !hasPatientMatchData && !featuresCancelled && !summaryCancelled && aiState === 'error' && (
              <RelevanceBadgeWrap>
                <AiErrorHeading><Icon icon={ICON_ERROR_HEX} /> Error loading AI information.</AiErrorHeading>
                {canRetry && <TryAgainLink css="padding-left:20px;" onClick={retryAll}>Try again</TryAgainLink>}
              </RelevanceBadgeWrap>
            )}
            {patientMatchLoading && !featuresCancelled && (
              <RelevanceBadgeWrap>
                <RelevanceLabel css="color:#999999;font-style:normal;"><Icon icon={"stars-circle"} /> Patient match score is processing<AnimatedDots /></RelevanceLabel>
                {showFeatureCancelLink && !relevancyLoading && <TryAgainLink css="padding-left:20px;" onClick={cancelFeatures}>Cancel</TryAgainLink>}
              </RelevanceBadgeWrap>
            )}
            {hasPatientMatchData && (
              <RelevanceBadgeWrap>
                <div style={{display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', position: 'relative'}}>
                  <RelevanceLabel><Icon icon={"stars-circle"} /> Patient match score</RelevanceLabel>
                </div>
                <RelevanceBarRow>
                  <div>
                    <RelevanceBarSvg score={patientMatchConfidence == null ? 0 : patientMatchConfidence} />
                    <RelevanceBarLabels>
                      <RelevanceBarLabel>Low</RelevanceBarLabel>
                      <RelevanceBarLabel>High</RelevanceBarLabel>
                    </RelevanceBarLabels>
                  </div>
                  {patientMatchExplanation && (
                    <RelevancyExplanationChip
                      ref={pmChipRef}
                      onClick={() => {
                        if (isPmExplanationOpen) {
                          setIsPmExplanationOpen(false)
                          return
                        }
                        if (!pmChipRef.current) return
                        const rect = pmChipRef.current.getBoundingClientRect()
                        const spaceBelow = window.innerHeight - rect.bottom - 16
                        const flipAbove = spaceBelow < 220
                        setPmPopoverPos({
                          top: flipAbove ? undefined : rect.bottom + 8,
                          bottom: flipAbove ? (window.innerHeight - rect.top + 8) : undefined,
                          left: rect.left - 230,
                          flipped: flipAbove,
                        })
                        setIsPmExplanationOpen(true)
                      }}
                    >
                      ?
                    </RelevancyExplanationChip>
                  )}
                </RelevanceBarRow>
                {isPmExplanationOpen && pmPopoverPos && ReactDOM.createPortal(
                  <div
                    ref={pmPopoverRef}
                    style={{
                      position: 'fixed',
                      top: pmPopoverPos.top,
                      bottom: pmPopoverPos.bottom,
                      left: pmPopoverPos.left,
                      zIndex: 99999,
                      width: 260,
                    }}
                  >
                    <RelevancyExplanationPopover flipped={pmPopoverPos.flipped}>
                      <RelevancyExplanationText>{patientMatchExplanation}</RelevancyExplanationText>
                      {!hideAiFeedbacks && (
                        <ExplanationRatingRow>
                          <ExplanationRatingLabel>Is this helpful?</ExplanationRatingLabel>
                          <ExplanationThumbsRow>
                            <ExplanationThumbBtn
                              type="button"
                              aria-label="Rate patient match explanation as helpful"
                              onClick={() => {
                                const next = patientMatchRating === 'up' ? null : 'up'
                                handleExplanationRating('patient-match', next, patientMatchExplanation)
                              }}
                              style={{background: patientMatchRating === 'up' ? '#DFEDF4' : undefined}}
                            >
                              <Icon icon={ICON_THUMBS_UP} size={12} color="#008DD0" />
                            </ExplanationThumbBtn>
                            <ExplanationThumbBtn
                              type="button"
                              aria-label="Rate patient match explanation as not helpful"
                              onClick={() => {
                                const next = patientMatchRating === 'down' ? null : 'down'
                                handleExplanationRating('patient-match', next, patientMatchExplanation)
                              }}
                              style={{background: patientMatchRating === 'down' ? '#DFEDF4' : undefined}}
                            >
                              <Icon icon={ICON_THUMBS_DOWN} size={12} color="#008DD0" />
                            </ExplanationThumbBtn>
                          </ExplanationThumbsRow>
                        </ExplanationRatingRow>
                      )}
                    </RelevancyExplanationPopover>
                  </div>,
                  document.body
                )}
                {/* {patientMismatch && (
                  <PatientMismatchBody>
                    <PatientMismatchGray>This record </PatientMismatchGray>
                    <PatientMismatchBold>does not</PatientMismatchBold>
                    <PatientMismatchGray> seem to be of the case's patient.</PatientMismatchGray>
                  </PatientMismatchBody>
                )} */}
              </RelevanceBadgeWrap>
            )}
            {relevancyLoading && !featuresCancelled && (
              <RelevanceBadgeWrap>
                <RelevanceLabel css="color:#999999;font-style:normal;"><Icon icon={"stars-circle"} /> Relevance to the case is processing<AnimatedDots /></RelevanceLabel>
                {showFeatureCancelLink && <TryAgainLink css="padding-left:20px;" onClick={cancelFeatures}>Cancel</TryAgainLink>}
              </RelevanceBadgeWrap>
            )}
            {hasRelevancyData && (
              <RelevanceBadgeWrap>
                <div style={{display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', position: 'relative'}}>
                  <RelevanceLabel><Icon icon={"stars-circle"} /> Relevance to the case</RelevanceLabel>
                </div>
                <RelevanceBarRow>
                  <div>
                    <RelevanceBarSvg score={relevanceScore == null ? 0 : relevanceScore} />
                    <RelevanceBarLabels>
                      <RelevanceBarLabel>Low</RelevanceBarLabel>
                      <RelevanceBarLabel>High</RelevanceBarLabel>
                    </RelevanceBarLabels>
                  </div>
                  {relevancyExplanation && (
                    <RelevancyExplanationChip
                      ref={chipRef}
                      onClick={() => {
                        if (isExplanationPopoverOpen) {
                          setIsExplanationPopoverOpen(false)
                          return
                        }
                        if (!chipRef.current) return
                        const rect = chipRef.current.getBoundingClientRect()
                        const spaceBelow = window.innerHeight - rect.bottom - 16
                        const flipAbove = spaceBelow < 220
                        setPopoverPos({
                          top: flipAbove ? undefined : rect.bottom + 8,
                          bottom: flipAbove ? (window.innerHeight - rect.top + 8) : undefined,
                          left: rect.left - 230,
                          flipped: flipAbove,
                        })
                        setIsExplanationPopoverOpen(true)
                      }}
                    >
                      ?
                    </RelevancyExplanationChip>
                  )}
                </RelevanceBarRow>
                {isExplanationPopoverOpen && popoverPos && ReactDOM.createPortal(
                  <div
                    ref={popoverRef}
                    style={{
                      position: 'fixed',
                      top: popoverPos.top,
                      bottom: popoverPos.bottom,
                      left: popoverPos.left,
                      zIndex: 99999,
                      width: 260,
                    }}
                  >
                    <RelevancyExplanationPopover flipped={popoverPos.flipped}>
                      <RelevancyExplanationText>{relevancyExplanation}</RelevancyExplanationText>
                      {!hideAiFeedbacks && (
                        <ExplanationRatingRow>
                          <ExplanationRatingLabel>Is this helpful?</ExplanationRatingLabel>
                          <ExplanationThumbsRow>
                            <ExplanationThumbBtn
                              type="button"
                              aria-label="Rate relevancy explanation as helpful"
                              onClick={() => {
                                const next = relevancyRating === 'up' ? null : 'up'
                                handleExplanationRating('relevancy', next, relevancyExplanation)
                              }}
                              style={{background: relevancyRating === 'up' ? '#DFEDF4' : undefined}}
                            >
                              <Icon icon={ICON_THUMBS_UP} size={12} color="#008DD0" />
                            </ExplanationThumbBtn>
                            <ExplanationThumbBtn
                              type="button"
                              aria-label="Rate relevancy explanation as not helpful"
                              onClick={() => {
                                const next = relevancyRating === 'down' ? null : 'down'
                                handleExplanationRating('relevancy', next, relevancyExplanation)
                              }}
                              style={{background: relevancyRating === 'down' ? '#DFEDF4' : undefined}}
                            >
                              <Icon icon={ICON_THUMBS_DOWN} size={12} color="#008DD0" />
                            </ExplanationThumbBtn>
                          </ExplanationThumbsRow>
                        </ExplanationRatingRow>
                      )}
                    </RelevancyExplanationPopover>
                  </div>,
                  document.body
                )}
              </RelevanceBadgeWrap>
            )}
            {/* Individual feature error handling when aiState is 'done' */}
            {aiState === 'done' && patientMatchError && relevancyError && (
              <RelevanceBadgeWrap>
                <AiErrorHeading><Icon icon={ICON_ERROR_HEX} /> Error loading AI features</AiErrorHeading>
                <AiErrorText>{patientMatchError}</AiErrorText>
                <TryAgainLink onClick={retryBothFeatures}>Try again</TryAgainLink>
              </RelevanceBadgeWrap>
            )}
            {aiState === 'done' && patientMatchError && !relevancyError && !hasPatientMatchData && (
              <RelevanceBadgeWrap>
                <AiErrorHeading><Icon icon={ICON_ERROR_HEX} /> Error loading patient match</AiErrorHeading>
                <AiErrorText>{patientMatchError}</AiErrorText>
                <TryAgainLink onClick={retryPatientMatch}>Try again</TryAgainLink>
              </RelevanceBadgeWrap>
            )}
            {aiState === 'done' && relevancyError && !patientMatchError && !hasRelevancyData && (
              <RelevanceBadgeWrap>
                <AiErrorHeading><Icon icon={ICON_ERROR_HEX} /> Error loading relevancy</AiErrorHeading>
                <AiErrorText>{relevancyError}</AiErrorText>
                <TryAgainLink onClick={retryRelevancy}>Try again</TryAgainLink>
              </RelevanceBadgeWrap>
            )}
          </DetailsRightCol>
            </DetailsPanel>
          </DetailsPanelInner>
        </DetailsPanelOuter>
      )}

      {/* Author strip */}
      {uploadDate && !isMinified && !isSelectingRecordsDirectMessage && (
        <AuthorStrip unavailable={record.quarantined || record.partiallyQuarantined}>
          Uploaded: <strong>{uploadDate}</strong> {submittedBy && (
            <span>by <strong>{submittedBy}</strong></span>
          )}
        </AuthorStrip>
      )}

      {/* Quarantined footer */}
      {(record.quarantined || record.partiallyQuarantined) && (
        <RecordRowQuarantinedFooter withMoveHandler={!isDragDisabled} />
      )}
    </RowContent>

    {/* AI Summary modal */}
    {isModalOpen && (
      <AiSummaryModal
        paragraphs={summaryParagraphs.length ? summaryParagraphs : ['No summary available.']}
        summaryData={summaryData}
        record={record}
        onClose={() => setIsModalOpen(false)}
        onOpenPage={openViewerAtPage}
        eveDocumentId={eveDocumentId}
        authJwt={worklistViewerJwt}
      />
    )}

    {!hideAiFeedbacks && feedbackState !== 'closed' && ReactDOM.createPortal(
      <FeedbackPanel
        rating={feedbackRating}
        state={feedbackState}
        text={feedbackMessage}
        onTextChange={setFeedbackMessage}
        onSubmit={submitExplanationFeedback}
        onCancel={closeFeedbackPopup}
        onClose={closeFeedbackPopup}
        onBack={backToFeedbackForm}
        overlayStyle={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          zIndex: 100000,
        }}
      />,
      document.body
    )}
  </CardShell>
}
